import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import redditApi from '../../../services/redditApi';

/* ─── Portal-based custom select (avoids overflow-x:hidden positioning bug) ── */

function Select({ value, onChange, options, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const selected = options.find((o) => o.value === value);

  function openMenu() {
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = Math.max(rect.width, 200);
    const spaceBelow = window.innerHeight - rect.bottom;
    const menuHeight = Math.min(options.length * 40 + 8, 260);
    const openUpward = spaceBelow < menuHeight && rect.top > menuHeight;
    const wouldOverflowRight = rect.left + menuWidth > window.innerWidth - 12;
    setMenuStyle({
      position: 'fixed',
      width: menuWidth,
      zIndex: 9999,
      ...(wouldOverflowRight ? { right: window.innerWidth - rect.right, left: 'auto' } : { left: rect.left }),
      ...(openUpward ? { bottom: window.innerHeight - rect.top + 6 } : { top: rect.bottom + 6 }),
    });
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    }
    function handleScroll() { setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  return (
    <div className={`rh-custom-select${open ? ' rh-custom-select--open' : ''}`} style={{ width: '100%' }}>
      <button
        type="button"
        ref={triggerRef}
        className="rh-custom-select-trigger"
        disabled={disabled}
        style={{ width: '100%' }}
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        <span>{selected ? selected.label : placeholder}</span>
        <svg className="rh-custom-select-chevron" width="12" height="12" fill="none"
          stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && createPortal(
        <div className="rh-custom-select-menu" ref={menuRef} style={menuStyle}>
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`rh-custom-select-option${value === o.value ? ' selected' : ''}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {value === o.value && (
                <svg width="12" height="12" fill="none" stroke="currentColor"
                  strokeWidth="2.5" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
              {o.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

function reminderBadge(level) {
  if (level === 0) return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: '#f3f4f6', color: '#6b7280',
      borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 500,
    }}>
      None sent
    </span>
  );
  if (level === 1) return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: '#fefce8', color: '#92400e',
      border: '1px solid #fde68a',
      borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600,
    }}>
      Reminder 1
    </span>
  );
  if (level === 2) return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: '#fff7ed', color: '#c2410c',
      border: '1px solid #fed7aa',
      borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600,
    }}>
      Reminder 2
    </span>
  );
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: '#fef2f2', color: '#b91c1c',
      border: '1px solid #fecaca',
      borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700,
    }}>
      Reminder 3 (CC'd)
    </span>
  );
}

function daysOverdueBadge(days) {
  const color = days >= 7 ? '#b91c1c' : days >= 3 ? '#c2410c' : '#92400e';
  const bg = days >= 7 ? '#fef2f2' : days >= 3 ? '#fff7ed' : '#fefce8';
  return (
    <span style={{
      fontWeight: 700, fontSize: 13, color, background: bg,
      borderRadius: 6, padding: '2px 8px',
    }}>
      {days}d
    </span>
  );
}

function exportCSV(rows, hasRedditorCol) {
  const headers = [
    ...(hasRedditorCol ? ['Redditor', 'Redditor Email'] : []),
    'Client', 'Post Title', 'Comment Date', 'Days Overdue', 'Reminders Sent',
  ];
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [
    headers.join(','),
    ...rows.map((r) => [
      ...(hasRedditorCol ? [escape(r.redditor_name), escape(r.redditor_email)] : []),
      escape(r.client_name),
      escape(r.post_title),
      escape(r.publishing_date),
      r.days_overdue,
      r.reminders_sent,
    ].join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `overdue-report-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const PAGE_SIZE = 30;

export default function OverdueReportTab() {
  const [redditors, setRedditors] = useState([]);
  const [selectedRedditor, setSelectedRedditor] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  // Load redditor list for the filter
  useEffect(() => {
    redditApi.get('/admin/redditors/')
      .then(({ data }) => {
        const active = (data || []).filter((r) => !r.is_admin && !r.is_accountant);
        setRedditors(active);
      })
      .catch(() => {});
  }, []);

  // Fetch report whenever the redditor filter changes
  useEffect(() => {
    setLoading(true);
    setError('');
    setSelectedClient('');
    const params = selectedRedditor ? `?redditor_id=${selectedRedditor}` : '';
    redditApi.get(`/admin/overdue-report/${params}`)
      .then(({ data }) => setReport(data))
      .catch(() => setError('Failed to load overdue report.'))
      .finally(() => setLoading(false));
  }, [selectedRedditor]);

  const allResults = report?.results || [];

  // Derive unique client names from loaded results for the client filter
  const clientOptions = [...new Set(allResults.map((r) => r.client_name))].sort();

  // Apply client filter client-side
  const results = selectedClient
    ? allResults.filter((r) => r.client_name === selectedClient)
    : allResults;

  const count = results.length;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const paginated  = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [selectedRedditor, selectedClient]);

  // Derive unique redditors in the filtered set for the summary
  const uniqueRedditors = new Set(results.map((r) => r.redditor_id).filter(Boolean));

  const summaryText = selectedRedditor
    ? `${count} overdue comment${count !== 1 ? 's' : ''} for ${redditors.find((r) => String(r.id) === selectedRedditor)?.full_name || 'this redditor'}`
    : `${count} overdue comment${count !== 1 ? 's' : ''} across ${uniqueRedditors.size} redditor${uniqueRedditors.size !== 1 ? 's' : ''}`;

  return (
    <div className="rh-tab-scroll">
      <div className="rh-page-header">
        <h2 className="rh-page-title">Overdue Report</h2>
        <p className="rh-page-subtitle">Scheduled comments that have passed their due date</p>
      </div>

      {/* Toolbar — single compact horizontal row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>

        {/* Redditor filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--rh-text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
            Redditor
          </label>
          <div style={{ width: 220 }}>
            <Select
              value={selectedRedditor}
              onChange={setSelectedRedditor}
              placeholder="All Redditors"
              options={[
                { value: '', label: 'All Redditors' },
                ...redditors.map((r) => ({ value: String(r.id), label: r.full_name })),
              ]}
            />
          </div>
        </div>

        {/* Client filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--rh-text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
            Client
          </label>
          <div style={{ width: 220 }}>
            <Select
              value={selectedClient}
              onChange={setSelectedClient}
              placeholder="All Clients"
              disabled={clientOptions.length === 0}
              options={[
                { value: '', label: 'All Clients' },
                ...clientOptions.map((c) => ({ value: c, label: c })),
              ]}
            />
          </div>
        </div>

        {!loading && count > 0 && (
          <button
            className="rh-overdue-export-btn"
            onClick={() => exportCSV(results, !selectedRedditor)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'none', border: '1px solid var(--rh-border)', borderRadius: 8,
              padding: '7px 14px', fontSize: 13, color: 'var(--rh-text-muted)',
              cursor: 'pointer', flexShrink: 0, marginLeft: 'auto',
            }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export CSV
          </button>
        )}
      </div>

      {/* Summary banner */}
      {!loading && report && (
        <div style={{
          background: count === 0 ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${count === 0 ? '#bbf7d0' : '#fecaca'}`,
          borderRadius: 10, padding: '12px 18px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          {count === 0 ? (
            <svg width="18" height="18" fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" /><polyline points="9 12 11 14 15 10" />
            </svg>
          ) : (
            <svg width="18" height="18" fill="none" stroke="#dc2626" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
          <span style={{
            fontSize: 14, fontWeight: 600,
            color: count === 0 ? '#15803d' : '#b91c1c',
          }}>
            {count === 0 ? 'No overdue comments — all caught up!' : summaryText}
          </span>
        </div>
      )}

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
          padding: '12px 18px', color: '#dc2626', marginBottom: 20,
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--rh-text-muted)', fontSize: 14 }}>
          Loading…
        </div>
      ) : (
        <div className="rh-card">
          {count === 0 ? (
            <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--rh-text-muted)', fontSize: 14 }}>
              No overdue comments to display.
            </div>
          ) : (
            <>
            <div className="rh-table-wrap">
              <table className="rh-table">
                <thead>
                  <tr>
                    {!selectedRedditor && <th>Redditor</th>}
                    <th>Client</th>
                    <th>Post Title</th>
                    <th>Comment Date</th>
                    <th>Days Overdue</th>
                    <th>Reminders Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((row) => (
                    <tr key={row.id} style={{
                      background: row.reminders_sent >= 3 ? '#fff8f8' : 'transparent',
                    }}>
                      {!selectedRedditor && (
                        <td style={{ fontWeight: 600, fontSize: 13 }}>
                          {row.redditor_name}
                        </td>
                      )}
                      <td style={{ fontSize: 13 }}>{row.client_name}</td>
                      <td style={{
                        fontSize: 12, color: 'var(--rh-text-muted)',
                        maxWidth: 320, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                        title={row.post_title}
                      >
                        {row.post_title}
                      </td>
                      <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                        {new Date(row.publishing_date).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                        })}
                      </td>
                      <td>{daysOverdueBadge(row.days_overdue)}</td>
                      <td>{reminderBadge(row.reminders_sent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '12px 16px', borderTop: '1px solid var(--rh-border)' }}>
                <button
                  type="button"
                  className="rh-btn rh-btn-secondary"
                  style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                  disabled={page === 1}
                  onClick={() => setPage(1)}
                >
                  «
                </button>
                <button
                  type="button"
                  className="rh-btn rh-btn-secondary"
                  style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ‹
                </button>
                <span style={{ fontSize: '0.8rem', color: 'var(--rh-text-muted)', whiteSpace: 'nowrap', padding: '0 6px' }}>
                  Page {page} of {totalPages} &nbsp;·&nbsp; {count} comment{count !== 1 ? 's' : ''}
                </span>
                <button
                  type="button"
                  className="rh-btn rh-btn-secondary"
                  style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  ›
                </button>
                <button
                  type="button"
                  className="rh-btn rh-btn-secondary"
                  style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                  disabled={page === totalPages}
                  onClick={() => setPage(totalPages)}
                >
                  »
                </button>
              </div>
            )}
            </>
          )}
        </div>
      )}

    </div>
  );
}
